import { RouteObject, Navigate, Routes } from 'react-router';
import { IS_APP_DEVELOPMENT } from '@/configs/app.config';
import { withSuspense, lazyLoad } from '@/components/_ui/suspense-handling';
import { ProtectedRoute } from '@/features/_shared/components/protected-route';
import { PublicRoute } from '@/features/_shared/components/public-route';
import { RoleHandlerRoute, FirstRedirectRoute } from '@/features/_shared/components/role-handler-route';
import { ALL_ROLES, NL_CARE_ROLES } from '@/features/user/constants/user-role.constant';
import { renderRoutes } from '@/configs/route.config';
import { memo } from 'react';
import { APP_ROUTES } from './app.route.constants';
import { ADMIN_ROLE, SUPERVISOR_ROLE, TICKETING_ADMIN_ROLE } from '@/features/users/user/constants/role.constants';
import CsatPage from './customers/csat-page';
// import SettingEditCustomerSurveyPage from './settings/setting-edit-customer-survey-page';

// TESTING
const SentryErrorExample = lazyLoad(() => import('@/components/sentry/sentry-error-example'));
const ComponentPage = lazyLoad(() => import('@/features/testing/components/component-page'));
const LayoutTestPage = lazyLoad(() => import('@/features/testing/components/layout-test-page'));

// DOCS
const ComponentDocsPage = lazyLoad(() => import('@/app/documentations/component-docs-page/component-docs-page'));

// GLOBAL
const AppLayout = lazyLoad(() => import('@/features/_shared/components/layout/app-layout'));

// ERROR
const Error403Page = lazyLoad(() => import('@/app/errors/error-403-page/error-403-page'));
const Error404Page = lazyLoad(() => import('@/app/errors/error-404-page/error-404-page'));

// AUTH
const LoginPage = lazyLoad(() => import('@/app/auths/login-page'));
const RegisterPage = lazyLoad(() => import('@/app/auths/register-page/register-page'));
const ForgotPasswordPage = lazyLoad(() => import('@/app/auths/forgot-password-page'));
const AcceptInvitationPage = lazyLoad(() => import('@/app/auths/accept-invitation-page/accept-invitation-page'));

// ANALYTICS
const AnalyticPage = lazyLoad(() => import('@/app/analytic-page'));

// CASES
const UnassignCasePage = lazyLoad(() => import('@/app/cases/unassign-case-page/unassign-case-page'));
const ActiveCasePage = lazyLoad(() => import('@/app/cases/active-case-page/active-case-page'));
const ResolvedCasePage = lazyLoad(() => import('@/app/cases/resolved-case-page/resolved-case-page'));

// CUSTOMERS
const CustomerLayout = lazyLoad(() => import('@/features/_shared/components/layout/customer-layout'));
const ListCustomerPage = lazyLoad(() => import('@/app/customers/list-customer-page'));
const DetailCustomerPageRefactor = lazyLoad(() => import('@/app/customers/detail-customer-page'));

// ANALYTICS
const AnalyticCasePage = lazyLoad(() => import('@/app/cases/analytic-case-page/analytic-case-page'));

// CHANNELS
const ChannelLayout = lazyLoad(() => import('@/features/_shared/components/layout/channel-layout'));
// social Media
const SocialMediaPage = lazyLoad(() => import('@/app/channels/sosial-media/sosial-media-channel-page'));
const ConnectMetaPage = lazyLoad(() => import('@/app/channels/sosial-media/meta/connect-meta-channel-page'));
const ConnectXPage = lazyLoad(() => import('@/app/channels/sosial-media/x/connect-x-channel-page'));
// chat Platform
const ChatPlatformPage = lazyLoad(() => import('@/app/channels/chat-platform/chat-platform-channel-page'));
const ConnectLinePage = lazyLoad(() => import('@/app/channels/chat-platform/line/connect-line-channel-page'));
const ConnectWhatsappPage = lazyLoad(
  () => import('@/app/channels/chat-platform/whatsapp/connect-whatsapp-channel-page')
);
// app Store
const ListAppStoreChannelPage = lazyLoad(
  () => import('@/app/channels/app-store/list-app-store-channel-page/list-app-store-channel-page')
);
const ConnectAppStoreChannelPage = lazyLoad(
  () => import('@/app/channels/app-store/add-app-store-channel-page/add-app-store-channel-page')
);
// google Play
const ListGooglePlayChannelPage = lazyLoad(
  () =>
    import('@/app/channels/google-play-store/list-google-play-store-channel-page/list-google-play-store-channel-page')
);
const ConnectGooglePlayChannelPage = lazyLoad(
  () => import('@/app/channels/google-play-store/add-google-play-store-channel-page/add-google-play-store-channel-page')
);
// google Review
const ListGoogleReviewChannelPage = lazyLoad(
  () => import('@/app/channels/google-review/list-google-review-channel-page/list-google-review-channel-page')
);
const ConnectGoogleReviewPage = lazyLoad(
  () => import('@/app/channels/google-review/add-google-review-channel-page/add-google-review-channel-page')
);
// Email
const ListEmailChannelPage = lazyLoad(
  () => import('@/app/channels/email/list-email-channel-page/list-email-channel-page')
);
const ConnectEmailChannelPage = lazyLoad(
  () => import('@/app/channels/email/add-email-channel-page/add-email-channel-page')
);

// Youtube
const ListYoutubeChannelPage = lazyLoad(
  () => import('@/app/channels/youtube/list-youtube-channel-page/list-youtube-channel-page')
);
const ConnectYoutubeChannelPage = lazyLoad(
  () => import('@/app/channels/youtube/connect-youtube-channel-page/connect-youtube-channel-page')
);
// Tiktok
const ConnectTiktokChannelPage = lazyLoad(
  () => import('@/app/channels/sosial-media/tiktok/connect-tiktok-channel-page/connect-tiktok-channel-page')
);
// Threads
const ConnectThreadsChannelPage = lazyLoad(
  () => import('@/app/channels/sosial-media/threads/connect-threads-channel-page/connect-threads-channel-page')
);
// TICKETINGS
const TicketingLayout = lazyLoad(
  () => import('@/features/ticketings/_shared/components/ticketing-layout/ticketing-layout')
);
const TicketingPage = lazyLoad(() => import('@/app/ticketings/ticketing-page/_refactor'));
const InviteTicketingMemberPage = lazyLoad(() => import('@/app/ticketings/ticketing-member-invitation-page'));

// SETTINGS
const SettingLayout = lazyLoad(() => import('@/features/_shared/components/layout/setting-layout'));
const SettingLabelPage = lazyLoad(() => import('@/app/settings/setting-label-page/setting-label-page'));
const SettingCustomerSurveyPage = lazyLoad(
  () => import('@/app/settings/setting-customer-survey-page/setting-customer-survey-page')
);
const SettingAddCustomerSurveyPage = lazyLoad(
  () => import('@/app/settings/setting-add-customer-survey-page/setting-add-customer-survey-page')
);
const SettingEditCustomerSurveyPage = lazyLoad(
  () => import('@/app/settings/setting-edit-customer-survey-page/setting-edit-customer-survey-page')
);
const SettingConfigurationPage = lazyLoad(
  () => import('@/app/settings/setting-configuration-page/setting-configuration-page')
);
const SettingNotificationPage = lazyLoad(
  () => import('@/app/settings/setting-notification-page/setting-notification-page')
);
const SettingProfilePage = lazyLoad(() => import('@/app/settings/setting-profile-page/setting-profile-page'));
const SettingTicketingPage = lazyLoad(() => import('@/app/settings/setting-ticketing-page/setting-ticketing-page'));
const SettingAgentTeamPage = lazyLoad(() => import('@/app/settings/setting-agent-team-page/setting-agent-team-page'));
const SettingDetailAgentPage = lazyLoad(
  () => import('@/app/settings/detail-setting-agent-page/detail-setting-agent-page')
);
const SettingDetailTeamPage = lazyLoad(
  () => import('@/app/settings/detail-setting-team-page/detail-setting-team-page')
);
const SettingReplyTemplatePage = lazyLoad(
  () => import('@/app/settings/setting-reply-template-page/setting-reply-template-page')
);

// CUSTOMERS
const SuccessCSATPage = lazyLoad(() => import('@/app/customers/success-csat-page/success-csat-page'));

// ROUTES
const routes: RouteObject[] = [
  { path: APP_ROUTES.auth.login.path, element: <PublicRoute>{withSuspense(LoginPage)}</PublicRoute> },
  { path: APP_ROUTES.auth.signup.path, element: <PublicRoute>{withSuspense(RegisterPage)}</PublicRoute> },
  { path: APP_ROUTES.auth.forgotPassword.path, element: <PublicRoute>{withSuspense(ForgotPasswordPage)}</PublicRoute> },
  {
    path: APP_ROUTES.auth.acceptInvitation.path,
    element: <PublicRoute>{withSuspense(AcceptInvitationPage)}</PublicRoute>,
  },
  {
    path: APP_ROUTES.auth.memberInvitation.path,
    element: withSuspense(InviteTicketingMemberPage),
  },

  {
    path: APP_ROUTES.csat.path,
    element: withSuspense(CsatPage),
  },

  {
    path: APP_ROUTES.csat.successCSATPage.path,
    element: withSuspense(SuccessCSATPage),
  },

  {
    path: APP_ROUTES.app.root.path,
    element: <ProtectedRoute>{<AppLayout />}</ProtectedRoute>,
    children: [
      { index: true, element: <FirstRedirectRoute /> },
      {
        path: APP_ROUTES.cases.activeCases.path,
        element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ActiveCasePage)}</RoleHandlerRoute>,
      },
      {
        path: APP_ROUTES.cases.unassignedCases.path,
        element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(UnassignCasePage)}</RoleHandlerRoute>,
      },
      {
        path: APP_ROUTES.cases.closeCases.path,
        element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ResolvedCasePage)}</RoleHandlerRoute>,
      },
      {
        path: APP_ROUTES.customerManagement.root.path,
        element: withSuspense(CustomerLayout),
        children: [
          {
            index: true,
            element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ListCustomerPage)}</RoleHandlerRoute>,
          },
          {
            path: APP_ROUTES.customerManagement.detailCustomer.path,
            element: (
              <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(DetailCustomerPageRefactor)}</RoleHandlerRoute>
            ),
          },
        ],
      },
      {
        path: APP_ROUTES.cases.analytics.path,
        element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(AnalyticPage)}</RoleHandlerRoute>,
      },
      {
        path: APP_ROUTES.channelIntegrations.root.path,
        element: withSuspense(ChannelLayout),
        children: [
          {
            index: true,
            element: <Navigate to={APP_ROUTES.channelIntegrations.socialMedia.root.getHref('meta')} replace />,
          },
          {
            path: APP_ROUTES.channelIntegrations.socialMedia.root.path,
            children: [
              {
                index: true,
                element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(SocialMediaPage)}</RoleHandlerRoute>,
              },

              {
                path: APP_ROUTES.channelIntegrations.socialMedia.addMeta.path,
                element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectMetaPage)}</RoleHandlerRoute>,
              },

              {
                path: APP_ROUTES.channelIntegrations.socialMedia.addX.path,
                element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectXPage)}</RoleHandlerRoute>,
              },

              {
                path: APP_ROUTES.channelIntegrations.socialMedia.addTiktok.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectTiktokChannelPage)}</RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.channelIntegrations.socialMedia.addThreads.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectThreadsChannelPage)}</RoleHandlerRoute>
                ),
              },
            ],
          },
          {
            path: APP_ROUTES.channelIntegrations.chatPlatform.root.path,
            children: [
              {
                index: true,
                element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ChatPlatformPage)}</RoleHandlerRoute>,
              },
              {
                path: APP_ROUTES.channelIntegrations.chatPlatform.addLine.path,
                element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectLinePage)}</RoleHandlerRoute>,
              },
              {
                path: APP_ROUTES.channelIntegrations.chatPlatform.addWhatsapp.path,
                element: <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectWhatsappPage)}</RoleHandlerRoute>,
              },
            ],
          },
          {
            path: APP_ROUTES.channelIntegrations.googleReview.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ListGoogleReviewChannelPage)}</RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.channelIntegrations.googleReview.addGoogleReview.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectGoogleReviewPage)}</RoleHandlerRoute>
                ),
              },
            ],
          },

          {
            path: APP_ROUTES.channelIntegrations.googlePlayStore.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ListGooglePlayChannelPage)}</RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.channelIntegrations.googlePlayStore.addGooglePlayStore.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>
                    {withSuspense(ConnectGooglePlayChannelPage)}
                  </RoleHandlerRoute>
                ),
              },
            ],
          },
          {
            path: APP_ROUTES.channelIntegrations.appStore.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ListAppStoreChannelPage)}</RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.channelIntegrations.appStore.addAppStore.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectAppStoreChannelPage)}</RoleHandlerRoute>
                ),
              },
            ],
          },
          {
            path: APP_ROUTES.channelIntegrations.youtube.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ListYoutubeChannelPage)}</RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.channelIntegrations.youtube.addYoutube.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectYoutubeChannelPage)}</RoleHandlerRoute>
                ),
              },
            ],
          },
          {
            path: APP_ROUTES.channelIntegrations.email.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ListEmailChannelPage)}</RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.channelIntegrations.email.addEmail.path,
                element: (
                  <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(ConnectEmailChannelPage)}</RoleHandlerRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: APP_ROUTES.settings.root.path,
        element: withSuspense(SettingLayout),
        children: [
          { index: true, element: <Navigate to={APP_ROUTES.settings.profile.getHref()} replace /> },
          {
            path: APP_ROUTES.settings.profile.path,
            element: <RoleHandlerRoute roles={ALL_ROLES}>{withSuspense(SettingProfilePage)}</RoleHandlerRoute>,
          },
          {
            path: APP_ROUTES.settings.label.path,
            element: (
              <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                {withSuspense(SettingLabelPage)}
              </RoleHandlerRoute>
            ),
          },
          {
            path: APP_ROUTES.settings.customerSurvey.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                    {withSuspense(SettingCustomerSurveyPage)}
                  </RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.settings.customerSurvey.addCustomerSurvey.path,
                element: (
                  <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                    {withSuspense(SettingAddCustomerSurveyPage)}
                  </RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.settings.customerSurvey.editCustomerSurvey.path,
                element: (
                  <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                    {withSuspense(SettingEditCustomerSurveyPage)}
                  </RoleHandlerRoute>
                ),
              },
            ],
          },
          {
            path: APP_ROUTES.settings.manageAgent.root.path,
            children: [
              {
                index: true,
                element: (
                  <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                    {withSuspense(SettingAgentTeamPage)}
                  </RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.settings.manageAgent.detailAgent.path,
                element: (
                  <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                    {withSuspense(SettingDetailAgentPage)}
                  </RoleHandlerRoute>
                ),
              },
              {
                path: APP_ROUTES.settings.manageAgent.detailTeam.path,
                element: (
                  <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                    {withSuspense(SettingDetailTeamPage)}
                  </RoleHandlerRoute>
                ),
              },
            ],
          },
          {
            path: APP_ROUTES.settings.configurations.path,
            element: (
              <RoleHandlerRoute roles={[ADMIN_ROLE, SUPERVISOR_ROLE]}>
                {withSuspense(SettingConfigurationPage)}
              </RoleHandlerRoute>
            ),
          },
          {
            path: APP_ROUTES.settings.notification.path,
            element: <RoleHandlerRoute roles={ALL_ROLES}>{withSuspense(SettingNotificationPage)}</RoleHandlerRoute>,
          },
          {
            path: APP_ROUTES.settings.ticketing.path,
            element: (
              <RoleHandlerRoute roles={[...NL_CARE_ROLES, TICKETING_ADMIN_ROLE]}>
                {withSuspense(SettingTicketingPage)}
              </RoleHandlerRoute>
            ),
          },
          {
            path: APP_ROUTES.settings.replyTemplate.path,
            element: (
              <RoleHandlerRoute roles={NL_CARE_ROLES}>{withSuspense(SettingReplyTemplatePage)}</RoleHandlerRoute>
            ),
          },
        ],
      },
      {
        path: APP_ROUTES.ticketing.root.path,
        element: withSuspense(TicketingLayout),
        children: [
          {
            index: true,
            element: <RoleHandlerRoute roles={ALL_ROLES}>{withSuspense(TicketingPage)}</RoleHandlerRoute>,
          },
        ],
      },
    ],
  },

  ...(IS_APP_DEVELOPMENT
    ? [
        {
          path: '/docs',
          element: withSuspense(ComponentDocsPage),
        },
        {
          path: '/test',
          children: [
            { index: true, element: <Navigate to="/sentry" /> },
            { path: 'sentry', element: withSuspense(SentryErrorExample) },
            { path: 'component', element: withSuspense(ComponentPage) },
            { path: 'layout', element: withSuspense(LayoutTestPage) },
          ],
        },
      ]
    : []),
  { path: APP_ROUTES.error.forbidden.path, element: withSuspense(Error403Page) },
  { path: APP_ROUTES.error.notFound.path, element: withSuspense(Error404Page) },
  { path: '*', element: <Navigate to={APP_ROUTES.error.notFound.getHref()} /> },
];

// App Routes
const AppRoutes = memo(() => {
  return <Routes>{renderRoutes(routes)}</Routes>;
});

AppRoutes.displayName = 'AppRoutes';

export default AppRoutes;
